import { Construct } from "../features/dashboard/dashboardTypes";
import { apiSlice } from "./apiSlice";

export const dashboardApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getSexDashlet: builder.query({
            query: () => 'public/dashlets/participantsBySex',
            providesTags: (result, error, { database }) => [
                { type: 'Dashboard', id: 'sex' },
                { type: 'Database', id: database },
            ],
            transformResponse: (responseData: any) => {
                return Object.keys(responseData || {}).map((key: string) => ({
                    name: key,
                    value: Number(responseData[key])
                }));
            }
        }),
        getVisitDashlet: builder.query({
            query: () => 'public/dashlets/participantsByVisit',
            providesTags: (result, error, { database }) => [
                { type: 'Dashboard', id: 'visits' },
                { type: 'Database', id: database },
            ]
        }),
        getInstrumentDashlet: builder.query({
            query: () => 'public/dashlets/participantsByInstrument',
            providesTags: (result, error, { database }) => [
                { type: 'Dashboard', id: 'instrument' },
                { type: 'Database', id: database },
            ],
            transformResponse: (responseData: any) => {
                return Object.keys(responseData || {}).map((key: string) => ({
                    name: key,
                    value: Number(responseData[key])
                }));
            }
        }),
        getRepeatedDashlet: builder.query({
            query: () => 'public/dashlets/participantsByRepeatedInstrument',
            providesTags: (result, error, { database }) => [
                { type: 'Dashboard', id: 'repeated' },
                { type: 'Database', id: database },
            ]
        }),
        getConstructDashlet: builder.query({
            query: () => 'public/dashlets/participantsByConstruct',
            providesTags: (result, error, { database }) => [
                { type: 'Dashboard', id: 'construct' },
                { type: 'Database', id: database },
            ],
            transformResponse: (responseData: any): Construct[] => responseData.constructs
        }),
        getModalityDashlet: builder.query({
            query: () => 'public/dashlets/modalityByParticipant',
            providesTags: (result, error, { database }) => [
                { type: 'Dashboard', id: 'modality' },
                { type: 'Database', id: database },
            ],
        }),
        getCohortComposition: builder.query({
            query: () => 'public/cohortComposition',
            providesTags: () => [
                { type: 'Dashboard', id: 'cohortComposition' },
            ],
        }),
        getVisitByInstrument: builder.query({
            query: () => 'public/instrumentVisits',
            providesTags: (result, error, { database }) => [
                { type: 'Dashboard', id: 'instrumentVisits' },
                { type: 'Database', id: database },
            ],
        }),
        getVisitByImaging: builder.query({
            query: () => 'public/dashlets/modalityByParticipant',
            providesTags: (result, error, { database }) => [
                { type: 'Dashboard', id: 'modality' },
                { type: 'Database', id: database },
            ],
            transformResponse: (responseData: any) => {
                const data: any = {}
                if (!responseData.modalities) {
                    return data;
                }
                Object.keys(responseData.modalities).forEach((session: string) => {
                    Object.keys(responseData.modalities[session]).forEach((modality: string) => {
                        if (!data[modality]) {
                            data[modality] = {
                                data: [],
                                title: modality,
                                type: 'BAR'
                            }
                        }
                        data[modality].data.push({
                            value: responseData.modalities[session][modality],
                            label: session
                        })
                    })
                })
                return data;
            }
        }),
        getSessions: builder.query({
            query: () => 'public/dashlets/sessions',
        }),
        getVariableBreakdown: builder.query({
            query: ({ instrument }) => `qc/dashboard/variable-breakdown?instrument=${encodeURIComponent(instrument)}`,
            providesTags: (results, error, { database, instrument }) => {
                return [
                    { type: 'Dashboard', id: `variableBreakdown_${instrument}` },
                    { type: 'Database', id: database },
                ]
            },
        }),
        getInstrumentDD: builder.mutation({
            query: ({ database, instrument }) => {
                const body = {
                    database,
                    filterModel: {
                        table_name: {
                            values: [instrument],
                            filterType: "set"
                        }
                    },
                    groupKeys: [],
                    pivotCols: [],
                    pivotMode: false,
                    rowGroupCols: [],
                    sortModel: [],
                    startRow: 0,
                    valueCols: []
                }
                return {
                    url: 'public/datadict/server',
                    method: 'POST',
                    body: body,
                }
            },
            transformResponse(responseData: any) {
                const dataDictionary: any = {};
                responseData.rows.forEach((row: any) => {
                    dataDictionary[row.name] = row;
                    if (row.additionalOptions?.levels) {
                        const levels = JSON.parse(row.additionalOptions.levels);
                        const levelsMap: any = {}
                        levels.forEach((level: any) => {
                            levelsMap[level.value] = level;
                        });
                        dataDictionary[row.name].levels = levelsMap;
                    }
                });
                return dataDictionary;
            }
        })
    })
});

export const {
    useGetSexDashletQuery,
    useGetVisitDashletQuery,
    useGetInstrumentDashletQuery,
    useGetRepeatedDashletQuery,
    useGetConstructDashletQuery,
    useGetModalityDashletQuery,
    useGetCohortCompositionQuery,
    useGetVisitByInstrumentQuery,
    useGetVisitByImagingQuery,
    useGetSessionsQuery,
    useGetVariableBreakdownQuery,
    useGetInstrumentDDMutation
} = dashboardApiSlice;
